import { BaseComponent, BuildingModel, RoomModel } from '../../../types/protocols';
import { BaseType } from '../../../types/Enums';
import { ParkingArea, ParkingSpot } from '../../../types/parking';
import { StorageUnit } from '../../../types/storageUnits';
import { Unit } from '../../../types/units';
import {
    DrawingView,
    levelDrawingView,
    partitionForView,
    roofPlanDrawingView,
    satelliteDrawingView,
} from '../../../Helpers/componentDrawing';
import { hasRoofPlanView } from '../../../Helpers/roofPlan';
import { roomIsOnLevel } from '../../../Helpers/roomUtils';
import { areasOnLevel, sortedParkingSpots } from '../../../Helpers/parkingUtils';
import { effectiveUnitLocations, unitLevelKeys, unitsOnLevel } from '../../../Helpers/unitUtils';

/**
 * Pure view logic for the BuildingPage tabs (spec web/docs/specs/building-page-tabs.md):
 * which tab and drawing the URL points at, what a drawing shows and what still
 * belongs on it. No Firebase, no React.
 */

// ---------------------------------------------------------------- tabs

export type BuildingTab = 'ritningar' | 'komponenter';

export const BUILDING_TABS: { key: BuildingTab; label: string }[] = [
    { key: 'ritningar', label: 'Ritningar' },
    { key: 'komponenter', label: 'Komponenter' },
];

export const parseTab = (value: string | null): BuildingTab =>
    BUILDING_TABS.find(tab => tab.key === value)?.key ?? 'ritningar';

/** One item on a drawing or in the pane's list; clicking it opens its page. */
export type DrawingItemKind = 'component' | 'room' | 'spot' | 'unit' | 'storage';
export interface DrawingItem {
    kind: DrawingItemKind;
    key: string;
}

// ---------------------------------------------------------------- drawings

export const SATELLITE_ID = 'satellite';
export const ROOF_PLAN_ID = 'roofPlan';

/** The `?drawing=` value of a view: a level key (push keys never clash) or one of the two singletons. */
export const drawingId = (view: Pick<DrawingView, 'kind' | 'levelKey'>): string =>
    view.kind === 'level' ? view.levelKey ?? '' : view.kind === 'satellite' ? SATELLITE_ID : ROOF_PLAN_ID;

/**
 * The drawing switcher, in LevelNavigator's order: the satellite image (when
 * there is one), every level from the lowest to the highest, with or without a
 * drawing, then the takplan (when it has anything to show).
 */
export const switcherViews = (model: BuildingModel | undefined): DrawingView[] => {
    const structure = model?.structure;
    const views: DrawingView[] = [];
    const satellite = satelliteDrawingView(model);
    if (satellite) views.push(satellite);
    Object.entries(structure?.levels || {})
        .map(([key, level]) => (level.key ? level : { ...level, key }))
        .sort((a, b) => a.number - b.number || a.key.localeCompare(b.key))
        .forEach(level => views.push(levelDrawingView(level)));
    if (hasRoofPlanView(structure)) views.push(roofPlanDrawingView(structure));
    return views;
};

/**
 * Where Ritningar opens: the lowest level numbered 0 or higher; when every
 * level is below ground, the lowest one; without levels the takplan, then the
 * satellite image.
 */
export const defaultSwitcherView = (views: DrawingView[], model: BuildingModel | undefined): DrawingView | null => {
    const levels = views
        .filter(view => view.kind === 'level')
        .map(view => ({ view, number: model?.structure?.levels?.[view.levelKey ?? '']?.number ?? 0 }));
    const aboveGround = levels.filter(level => level.number >= 0).sort((a, b) => a.number - b.number);
    if (aboveGround.length > 0) return aboveGround[0].view;
    const below = [...levels].sort((a, b) => a.number - b.number);
    if (below.length > 0) return below[0].view;
    return views.find(view => view.kind === 'roofPlan') ?? views.find(view => view.kind === 'satellite') ?? null;
};

/** The view `?drawing=` names, else the default. */
export const resolveSwitcherView = (
    views: DrawingView[],
    id: string | null,
    model: BuildingModel | undefined
): DrawingView | null =>
    (id ? views.find(view => drawingId(view) === id) : undefined) ?? defaultSwitcherView(views, model);

/**
 * Marker layers of Ritningar; all on by default, `?hide=` lists the ones turned
 * off. Utilities are their own layer; `components` is every other type.
 */
export type DrawingLayer = 'rooms' | 'components' | 'utilities' | 'parking' | 'rentables';

export const DRAWING_LAYERS: { key: DrawingLayer; label: string }[] = [
    { key: 'rooms', label: 'Rum' },
    { key: 'components', label: 'Komponenter' },
    { key: 'utilities', label: 'Fastighetsteknik' },
    { key: 'parking', label: 'Parkeringsplatser' },
    { key: 'rentables', label: 'Lägenheter, lokaler & förråd' },
];

/** The layer a component is drawn on. */
export const componentLayer = (component: Pick<BaseComponent, 'type'>): DrawingLayer =>
    component.type === BaseType.Utility ? 'utilities' : 'components';

export const parseHiddenLayers = (value: string | null): Set<DrawingLayer> =>
    new Set(
        (value || '')
            .split(',')
            .map(part => DRAWING_LAYERS.find(layer => layer.key === part)?.key)
            .filter((layer): layer is DrawingLayer => !!layer)
    );

export const serializeHiddenLayers = (hidden: Set<DrawingLayer>): string | null =>
    hidden.size === 0 ? null : DRAWING_LAYERS.filter(layer => hidden.has(layer.key)).map(layer => layer.key).join(',');

/** Placed items are drawn; unplaced ones clearly belong to the drawing but carry no pin ("Ej placerade"). */
export interface Placement<T> {
    placed: T[];
    unplaced: T[];
}

export interface SpotEntry {
    spot: ParkingSpot;
    area: ParkingArea;
}

export interface DrawingContent {
    rooms: Placement<RoomModel>;
    components: Placement<BaseComponent>;
    spots: Placement<SpotEntry>;
    units: Placement<Unit>;
    storageUnits: Placement<StorageUnit>;
}

export interface RentableObjects {
    parkingAreas: ParkingArea[];
    storageUnits: StorageUnit[];
    units: Unit[];
}

const split = <T>(items: T[], isPlaced: (item: T) => boolean): Placement<T> => {
    const placed: T[] = [];
    const unplaced: T[] = [];
    items.forEach(item => (isPlaced(item) ? placed : unplaced).push(item));
    return { placed, unplaced };
};

const empty = <T>(): Placement<T> => ({ placed: [], unplaced: [] });

/** Live components with their keys filled in (archived ones are replaced and never listed). */
export const liveComponents = (model: BuildingModel | undefined): BaseComponent[] =>
    Object.entries(model?.structure?.components || {})
        .filter(([, component]) => !!component && !component.archivedAt)
        .map(([key, component]) => (component.key ? component : { ...component, key }));

/** Ytskikt (golv, innertak, innerväggar): never on Ritningar, they only add noise. */
const SURFACE_FINISH_TYPES = new Set<string>([BaseType.Floor, BaseType.Ceiling, BaseType.Wall]);

/**
 * Everything one drawing shows, and what belongs on it without a pin.
 *
 * Components: the homes of `componentDrawing` decide what belongs, except that
 * ytskikt are left out everywhere and a window or door is never listed as unplaced on a level (takfönster still
 * are, on the takplan): they show only where they are placed. Rooms, parking
 * spots, units and storage units only exist on level drawings. A room
 * spanning several levels is drawn on each but belongs, unplaced, only to its
 * primary level.
 */
export const drawingContent = (
    view: DrawingView,
    model: BuildingModel | undefined,
    objects: RentableObjects
): DrawingContent => {
    const structure = model?.structure;
    const imageKey = view.imageKey;
    const pinned = (locations: { [imageKey: string]: unknown } | undefined) => !!imageKey && !!locations?.[imageKey];

    const partition = partitionForView(
        liveComponents(model).filter(component => !SURFACE_FINISH_TYPES.has(component.type)),
        view,
        structure
    );
    const components: Placement<BaseComponent> = {
        placed: partition.placed,
        unplaced: partition.unplaced.filter(component =>
            view.kind === 'roofPlan' || (component.type !== BaseType.Window && component.type !== BaseType.Door)
        ),
    };

    if (view.kind !== 'level' || !view.levelKey) {
        return { rooms: empty(), components, spots: empty(), units: empty(), storageUnits: empty() };
    }
    const levelKey = view.levelKey;
    const allRooms = Object.entries(structure?.rooms || {})
        .filter(([, room]) => !!room)
        .map(([key, room]) => (room.key ? room : { ...room, key }));
    const roomsHere = allRooms.filter(room => roomIsOnLevel(room, levelKey));
    const rooms: Placement<RoomModel> = {
        placed: roomsHere.filter(room => pinned(room.blueprintLocations)),
        unplaced: roomsHere.filter(room => room.levelKey === levelKey && !pinned(room.blueprintLocations)),
    };

    const spots = split(
        areasOnLevel(objects.parkingAreas, structure, levelKey).flatMap(area =>
            sortedParkingSpots(area).map(spot => ({ spot, area }))
        ),
        entry => pinned(entry.spot.blueprintLocations)
    );
    const units = split(
        unitsOnLevel(objects.units, levelKey, structure?.rooms),
        unit => pinned(effectiveUnitLocations(unit, structure?.rooms))
    );
    const storageUnits = split(
        objects.storageUnits.filter(unit => unit.levelKey === levelKey),
        unit => pinned(unit.blueprintLocations)
    );
    return { rooms, components, spots, units, storageUnits };
};


// ---------------------------------------------------------------- item pages

/**
 * The page a drawing item opens, the same targets as LevelPage's markers; null
 * for a parking spot whose area is gone.
 */
export const drawingItemPath = (
    item: DrawingItem,
    buildingKey: string,
    model: BuildingModel | undefined,
    objects: RentableObjects
): string | null => {
    const base = `/building/${buildingKey}`;
    const key = encodeURIComponent(item.key);
    const levelParam = (levelKey: string | undefined) => (levelKey ? `level=${encodeURIComponent(levelKey)}&` : '');
    switch (item.kind) {
        case 'component':
            return `${base}/component/${item.key}`;
        case 'room':
            return `${base}/room/${item.key}`;
        case 'spot': {
            const area = objects.parkingAreas.find(a => !!a.spots?.[item.key]);
            return area ? `${base}/parking/${area.key}?spot=${key}` : null;
        }
        case 'unit': {
            const unit = objects.units.find(u => u.key === item.key);
            // The unit's lowest level, the one the units page lists it under first.
            const levels = model?.structure?.levels;
            const primaryLevel = unit
                ? unitLevelKeys(unit, model?.structure?.rooms)
                    .filter(levelKey => !!levels?.[levelKey])
                    .sort((a, b) => levels![a].number - levels![b].number)[0]
                : undefined;
            return `${base}/units?${levelParam(primaryLevel)}unit=${key}`;
        }
        case 'storage': {
            const unit = objects.storageUnits.find(u => u.key === item.key);
            return `${base}/storage-units?${levelParam(unit?.levelKey)}unit=${key}`;
        }
    }
};
